// Moved verbatim from `src/hooks/query/useQueryUserSnippets.ts` (now a pure
// shim) with IP2 (ARCFE-956). Token read severed onto the `useAuthToken`
// seam; cache commands run against the seam's queryClient (same app
// singleton). The total fetch (empty list on ANY error) and the
// `skipToken` gate are frozen behaviour.
import { skipToken, useQuery } from '@tanstack/react-query';

import { GET_USER_SNIPPETS } from '../../contracts/keys';
import { type SnippetsResponse } from '../../contracts/types/user-types';
import { getUserSnippets } from '../api/user';
import { getWorkspaceDataConfig } from '../config';
import { useQueryUser } from './useQueryUser';

export { GET_USER_SNIPPETS };

export const getCurrentUserSnippets = () =>
  getWorkspaceDataConfig().queryClient.getQueryData<SnippetsResponse>([
    GET_USER_SNIPPETS,
  ]);

// Total — resolves an empty list on any error so a snippets outage can never
// reject (and never yields undefined, which TanStack v5 rejects as query data).
const fetchUserSnippets = async (): Promise<SnippetsResponse> => {
  try {
    const { data } = await getUserSnippets();
    return { ...data };
  } catch {
    return { snippets: [] };
  }
};

// Awaitable cache-or-fetch so snippets are loaded before the synchronous read in buildUserContext.
export const ensureCurrentUserSnippets = async () =>
  await getWorkspaceDataConfig().queryClient.ensureQueryData<SnippetsResponse>({
    queryKey: [GET_USER_SNIPPETS],
    queryFn: fetchUserSnippets,
  });

export const invalidateCurrentUserSnippets = async () =>
  await getWorkspaceDataConfig().queryClient.invalidateQueries({
    queryKey: [GET_USER_SNIPPETS],
  });

export function useQueryUserSnippets() {
  const token = getWorkspaceDataConfig().useAuthToken();
  const { data: userData } = useQueryUser();

  return useQuery({
    queryKey: [GET_USER_SNIPPETS],
    queryFn: !!token && !!userData ? fetchUserSnippets : skipToken,
  });
}
